import { useEffect, useMemo, useState } from "react";
import { Badge } from "~/components/ui/badge";
import { useNavigate } from "react-router-dom";
import paths from "~/paths";
import SearchQuery, { SearchQueryJSON } from "~/searchQuery";
import { X } from "lucide-react";
import { observer } from "mobx-react";
import isSet from "lodash/isSet";
import { useQueryURLSearchParams } from "~/util/hooks";

const linkCls = "cursor-pointer text-sm text-primary-600 hover:underline";

const Tag = (props: { remove: () => void; text: string; title: string; canRemove: boolean }) => (
  <Badge variant="default" className="gap-1 px-3 py-1 text-xs" title={props.title}>
    <span>{props.text}</span>
    {props.canRemove && (
      <button
        type="button"
        aria-label="Remove this filter"
        onClick={props.remove}
        className="ml-1 inline-flex cursor-pointer items-center text-info-800/70 hover:text-info-800"
      >
        <X className="size-2.5" />
      </button>
    )}
  </Badge>
);

interface AFDProps {
  onSearch: (sq: SearchQuery) => void;
  searchQuery: SearchQuery | null;
}

const AdvancedFiltersDisplay = observer((props: AFDProps) => {
  const { onSearch, searchQuery } = props;

  const isTitleApplied = searchQuery ? searchQuery.isTitlePresent() : false;
  const n = (searchQuery ? searchQuery.numOfAppliedSearchFilters() : 0) + (isTitleApplied ? 1 : 0);
  const isThereAnyFilterApplied = n !== 0;

  const navigate = useNavigate();
  const goToAdvancedSearch = () => {
    navigate(paths.construct.advancedSearch(searchQuery));
  };
  const pageQuery = useQueryURLSearchParams();
  const [shown, setShown] = useState(true);
  const [queryInJson, setQueryInJson] = useState<SearchQueryJSON | null>(null);
  useEffect(() => {
    (async () => {
      if (searchQuery === null) return;
      // preload all required fields for display
      await searchQuery.requestAllMissingDataForQuery();
      const topics = pageQuery.get("topics");
      searchQuery.topics = new Set(topics ? topics.split(",").map((item) => +item) : []);
      const docTypes = pageQuery.get("docTypes");
      searchQuery.docTypes = new Set(docTypes ? docTypes.split(",").map((item) => +item) : []);
      setQueryInJson(searchQuery.toFullJSON());
    })();
  }, [searchQuery]);

  const filtersRendered = useMemo(() => {
    if (!shown || !queryInJson || !searchQuery) return null;
    const entries = Object.entries(queryInJson).map(([_fieldName, { value, label }], idx: number) => {
      const fieldName: keyof SearchQuery = _fieldName as any;
      // `title` is not an advanced filter but is still a user-applied filter shown as a tag
      const canRemove = searchQuery.isAdvancedFilterField(fieldName) || fieldName === "title";
      const title = label;
      if (value instanceof Array) {
        if (value.length === 0) return null;
        return value.map((a, idx) => (
          <Tag
            canRemove={canRemove}
            remove={() => onSearch(searchQuery.remove(fieldName, { idx }))}
            text={a}
            key={idx}
            title={title}
          />
        ));
      }

      if (isSet(value)) {
        if (value.size === 0) return null;
        return Array.from(value).map((a: string) => {
          return (
            <Tag
              canRemove={canRemove}
              remove={() => onSearch(searchQuery.remove(fieldName, { key: a }))}
              text={a}
              key={idx}
              title={title}
            />
          );
        });
      }

      if (!value) return null;

      return (
        <Tag
          canRemove={canRemove}
          remove={() => onSearch(searchQuery.remove(fieldName, {}))}
          text={value.toString()}
          key={idx}
          title={title}
        />
      );
    });
    return <div className="mt-1 flex flex-wrap gap-1">{entries}</div>;
  }, [shown, queryInJson]);

  return (
    <div className="whitespace-nowrap">
      {isThereAnyFilterApplied ? (
        <div className="flex flex-col items-start">
          <a
            href="#"
            className={linkCls}
            onClick={(e) => {
              e.preventDefault();
              setShown(!shown);
            }}
          >
            {shown ? "Hide advanced filters" : `Show search filters (${n} applied)`}
          </a>
          <a
            href="#"
            className={`${linkCls} mt-1`}
            onClick={(e) => {
              e.preventDefault();
              goToAdvancedSearch();
            }}
          >
            Edit advanced search
          </a>
          {filtersRendered}
        </div>
      ) : (
        <a
          href="#"
          className={linkCls}
          onClick={(e) => {
            e.preventDefault();
            goToAdvancedSearch();
          }}
        >
          Advanced search
        </a>
      )}
    </div>
  );
});

export default AdvancedFiltersDisplay;
